/**
 * A group's page as it last loaded, kept so it opens at once and offline (Sam, 2026-10-22: a saved group tapped offline
 * showed a red error instead of its page). In memory for this run, and in the query cache, which is saved to the phone,
 * so it survives a restart. The page (useGroupInfoPage) and the event-day download (eventDayOffline) load it the same way.
 */
import { supabase } from '../supabaseClient';
import {
  GROUP_ROW_SELECT,
  GROUP_EVENT_SELECT,
  GROUP_MEMBERS_SELECT,
  mapMembersRows,
} from './groupInfoQueries';

/** Each group page seen this run, as it last showed: a revisit shows it at once and refreshes behind it. */
const GROUP_PAGE_SNAPSHOTS = new Map();

export const groupPageKey = (groupId, userId) => ['groups', groupId, 'page', userId ?? 'anon'];

export function readGroupPage(queryClient, userId, groupId) {
  const mem = GROUP_PAGE_SNAPSHOTS.get(groupId);
  if (mem) return mem;
  const saved = queryClient.getQueryData(groupPageKey(groupId, userId));
  if (!saved?.group) return null;
  const snap = { ...saved, blocked: new Set(saved.blocked ?? []) };
  GROUP_PAGE_SNAPSHOTS.set(groupId, snap);
  return snap;
}

export function writeGroupPage(queryClient, userId, groupId, snap) {
  GROUP_PAGE_SNAPSHOTS.set(groupId, snap);
  queryClient.setQueryData(groupPageKey(groupId, userId), { ...snap, blocked: [...snap.blocked] });
}

export function dropGroupPage(queryClient, userId, groupId) {
  GROUP_PAGE_SNAPSHOTS.delete(groupId);
  queryClient.removeQueries({ queryKey: groupPageKey(groupId, userId), exact: true });
}

/** PostgREST's "no row" (RLS hides a group you are not in): the one failure that means the group is not yours to see.
 *  Anything else - a fetch that never reached the server - is the network, not the group. */
const isNotFound = (err) => err?.code === 'PGRST116';

/**
 * One round trip: the group with its event embedded, beside the count, the members and your blocks. It was the group
 * first and everything else after - two trips, about a second on a phone. The going count is separate (fetchGoingCount).
 *
 * { status: 'ok', snap, membersFetched } | { status: 'notFound' } | { status: 'offline' }. `prev` fills what was not
 * fetched (signed out: no members). Never throws.
 */
export async function fetchGroupPage(groupId, userId, prev = null) {
  try {
    const [groupRes, memberRpcRes, membersRes, blocksRes] = await Promise.all([
      supabase
        .from('groups')
        .select(`${GROUP_ROW_SELECT}, event:events(${GROUP_EVENT_SELECT})`)
        .eq('id', groupId)
        .single(),
      supabase.rpc('get_group_member_count', { group_uuid: groupId }),
      userId
        ? supabase
            .from('group_members')
            .select(GROUP_MEMBERS_SELECT)
            .eq('group_id', groupId)
            .is('left_at', null)
            .order('joined_at', { ascending: true })
        : Promise.resolve({ data: null, error: null }),
      userId
        ? supabase.from('user_blocks').select('blocked_user_id').eq('blocker_id', userId)
        : Promise.resolve({ data: null }),
    ]);
    const { data: groupRow, error: groupError } = groupRes;
    if (groupError && !isNotFound(groupError)) return { status: 'offline' };
    if (groupError || !groupRow) return { status: 'notFound' };
    const { event: eventRow, ...group } = groupRow;
    const members = membersRes.data ? mapMembersRows(membersRes.data) : (prev?.members ?? []);
    const blocked = blocksRes.data
      ? new Set(blocksRes.data.map((b) => b.blocked_user_id))
      : new Set();
    return {
      status: 'ok',
      membersFetched: Boolean(membersRes.data),
      snap: {
        group,
        event: eventRow || null,
        members,
        blocked,
        attendeeCount: memberRpcRes.data ?? 0,
        rsvpGoing: prev?.rsvpGoing ?? 0,
      },
    };
  } catch (err) {
    // supabase-js returns server errors rather than throwing them: a throw is the network.
    console.warn('[GroupPage] load failed:', err);
    return { status: 'offline' };
  }
}

/** How many are going to the group's event (the page does not wait for it). */
export async function fetchGoingCount(eventId) {
  const { count } = await supabase
    .from('rsvps')
    .select('*', { count: 'exact', head: true })
    .eq('event_id', eventId)
    .eq('status', 'going');
  return count ?? 0;
}
